import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import {
  CreateProductPriceRequest,
  EditProductPriceRequest,
  SaleProductPrice,
} from '../../../../clients/quipli-service/pricing/pricing';
import { Field, FieldLabel } from '../../../../components/field/field';
import { DatePicker } from '../../../../components/date-picker/date-picker';
import { Modal } from '../../../../components/modal/modal';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../../util/toasts';
import { ApiError } from '../../../../clients/axios-instance';
import { OrderType } from '../../../../clients/quipli-service/order/order';
import { Button } from '../../../../components/form/button';
import { TextArea } from '../../../../components/textarea/textarea';
import { SearchProduct } from '../components/search-product';
import { Product, ProductType } from '../../../../clients/quipli-service/product/product';
import { Radio } from '../../../../components/radio/radio';
import { User } from '../../../../clients/quipli-service/user/user';

export const UserSalePriceModal = ({
  user,
  price,
  isOpen,
  close,
}: {
  user: User;
  price?: SaleProductPrice;
  isOpen: boolean;
  close: (refresh?: boolean) => void;
}) => {
  const { createPrice, editPrice, getProduct } = useAPI();

  const initPriceState = {};
  const [productType, setProductType] = useState<ProductType>(ProductType.Product);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);
  const [priceState, setPriceState] = useState<{
    id?: string;
    price?: number;
    from?: DateTime;
    to?: DateTime;
    name?: string;
  }>(initPriceState);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (price) {
      setPriceState({
        id: price.id,
        price: price.price / 100,
        from: price.from,
        to: price.to,
        name: price.name,
      });

      getProduct(price.productId)
        .then((product) => {
          setSelectedProduct(product);
          setProductType(product.type);
        })
        .catch(() => {
          toast('Error retrieving product', 'error');
        });
    } else {
      resetState();
    }
  }, [price]);

  const isSaveDisabled: boolean = !selectedProduct || priceState.price === undefined;
  const resetState = () => {
    setPriceState(initPriceState);
    setProductType(ProductType.Product);
    setSelectedProduct(undefined);
  };

  const savePrice = () => {
    if (isSaveDisabled) {
      throw new Error('Missing required fields to save sale price');
    }

    setIsSaving(true);

    const body = {
      type: OrderType.Sale,
      productId: selectedProduct!.id,
      userId: user.id,
      price: +(priceState.price! * 100).toFixed(0),
      from: priceState.from?.startOf('day'),
      to: priceState.to?.endOf('day'),
      name: undefined,
      featured: true,
      locationIds: undefined,
    };

    if (price?.id) {
      editPrice(price.id, body as EditProductPriceRequest)
        .then(() => {
          toast('Successfully updated sale price', 'success');
          resetState();
          close(true);
        })
        .catch((err: ApiError) => {
          const errMsg = err.status === 400 && err.message ? err.message : 'Error updating sale price';
          toast(errMsg, 'error');
        });
    } else {
      createPrice(body as CreateProductPriceRequest)
        .then(() => {
          toast('Successfully created sale price', 'success');
          resetState();
          close(true);
        })
        .catch((err: ApiError) => {
          const errMsg = err.status === 400 && err.message ? err.message : 'Error creating sale price';
          toast(errMsg, 'error');
        });
    }

    setIsSaving(false);
  };

  const cancel = () => {
    resetState();
    close();
  };

  return (
    <div className="sale-price-modal">
      <Modal isOpen={isOpen} title={`${price === undefined ? 'Add' : 'Edit'} user sale price`} close={cancel}>
        <div className="space-y-5 edit-sale-price-container font-sans">
          {!price && (
            <div className="flex gap-5 my-2">
              <div className="flex gap-1 items-center">
                <Radio
                  isChecked={productType === ProductType.Product}
                  onChange={() => setProductType(ProductType.Product)}
                />
                <FieldLabel label="Product" />
              </div>
              <div className="flex gap-1 items-center">
                <Radio isChecked={productType === ProductType.Part} onChange={() => setProductType(ProductType.Part)} />
                <FieldLabel label="Part" />
              </div>
              <div className="flex gap-1 items-center">
                <Radio
                  isChecked={productType === ProductType.Service}
                  onChange={() => setProductType(ProductType.Service)}
                />
                <FieldLabel label="Service" />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-[4px]">
            <FieldLabel
              label={
                productType === ProductType.Product ? 'Product' : productType === ProductType.Part ? 'Part' : 'Service'
              }
              isRequired
            />
            {price && selectedProduct ? (
              <TextArea className="w-full h-full resize-none text-sm" value={selectedProduct.name} disabled={true} />
            ) : (
              <SearchProduct
                value={selectedProduct}
                onSelect={setSelectedProduct}
                productType={productType}
                placeholder={
                  productType === ProductType.Product
                    ? 'Search products'
                    : ProductType.Part
                    ? 'Search parts'
                    : 'Search services'
                }
                isForSale={true}
              />
            )}
          </div>

          <div>
            <Field
              type="text"
              label="Name"
              value={priceState.name}
              onChange={(name: string) => setPriceState({ ...priceState, name })}
            />
          </div>

          <div className="grid grid-cols-5 items-center gap-2">
            <div className="col-span-2 ml-1">
              <span className="text-sm font-semibold text-gray-500">Sale Price, $*</span>
            </div>
            <div className="col-span-3">
              <Field
                className="price-field"
                type="number"
                value={priceState.price}
                onChange={(p: number) => {
                  setPriceState({ ...priceState, price: p });
                }}
                maximumFractionDigits={2}
              />
            </div>
          </div>

          <div className="grid grid-cols-5 items-center gap-2">
            <div className="col-span-2 ml-1">
              <span className="text-sm font-semibold text-gray-500">Starts On</span>
            </div>
            <div className="col-span-3">
              <DatePicker value={priceState.from} onChange={(from) => setPriceState({ ...priceState, from })} />
            </div>
          </div>

          <div className="grid grid-cols-5 items-center gap-2">
            <div className="col-span-2 ml-1">
              <span className="text-sm font-semibold text-gray-500">Ends On</span>
            </div>
            <div className="col-span-3">
              <DatePicker value={priceState.to} onChange={(to) => setPriceState({ ...priceState, to })} />
            </div>
          </div>
        </div>

        <div className="inset-x-0 bottom-0 mt-6 flex justify-end button-group">
          <Button className="button-secondary" onClick={cancel}>
            Cancel
          </Button>
          <Button className="button-primary" onClick={savePrice} disabled={isSaveDisabled || isSaving}>
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
};
